import { Badge, Empty, Segmented, Spin, Tag } from 'antd';
import {
  LoadingOutlined,
  PhoneOutlined,
  RobotOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { AiCallVo } from '@/facilities/modules/aiCall';
import {
  callerLabel,
  formatDuration,
  formatTs,
  isTransferred,
  takeoverInProgress,
} from './utils';
import { LiveBadge } from './LiveBits';
import { isLive } from './liveUtils';

export type CallListTab = 'PENDING_REVIEW' | 'REVIEWED';

interface Props {
  calls: AiCallVo[];
  activeId?: number | null;
  tab: CallListTab;
  pendingCount: number;
  loading: boolean;
  onTabChange: (tab: CallListTab) => void;
  onSelect: (call: AiCallVo) => void;
}

const statusTag = (call: AiCallVo, t: (k: string) => string) => {
  switch (call.status) {
    case 'IN_PROGRESS':
      return <LiveBadge since={call.startedAt || call.createdAt} />;
    case 'SUBMITTED':
      return (
        <>
          <Tag color="success" bordered={false} className="rounded-full">
            {call.caseNo
              ? `${t('hotline_submitted')} · ${call.caseNo}`
              : t('hotline_submitted')}
          </Tag>
          {call.caseStatus && (
            <Tag bordered={false} className="rounded-full">
              {t('hotline_case')} · {t(call.caseStatus)}
            </Tag>
          )}
        </>
      );
    case 'DISMISSED':
      return (
        <Tag bordered={false} className="rounded-full">
          {t('hotline_dismissed')}
        </Tag>
      );
    default:
      return (
        <Tag color="warning" bordered={false} className="rounded-full">
          {t('hotline_pendingReview')}
        </Tag>
      );
  }
};

export default function CallList({
  calls,
  activeId,
  tab,
  pendingCount,
  loading,
  onTabChange,
  onSelect,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-full bg-[#fff] dark:bg-[#141414] border-r border-[#E8E9ED] dark:border-[#ffffff21]">
      <div className="px-4 pt-4 pb-3 border-b border-[#E8E9ED] dark:border-[#ffffff21]">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[18px] font-semibold flex items-center gap-2">
            <PhoneOutlined /> {t('hotline_calls')}
          </h2>
          {loading && <Spin size="small" />}
        </div>
        <Segmented
          block
          value={tab}
          onChange={(v) => onTabChange(v as CallListTab)}
          options={[
            {
              value: 'PENDING_REVIEW',
              label: (
                <span className="flex items-center justify-center gap-2">
                  {t('hotline_pendingReview')}
                  <Badge count={pendingCount} size="small" overflowCount={99} />
                </span>
              ),
            },
            { value: 'REVIEWED', label: t('hotline_reviewed') },
          ]}
        />
      </div>
      <div className="flex-1 overflow-y-auto">
        {calls.length === 0 && !loading && (
          <Empty className="mt-16" description={t('hotline_noCalls')} />
        )}
        {calls.map((call) => {
          const active = call.id === activeId;
          const live = isLive(call);
          const incomplete = !live && call.formData?.complete === false;
          const preview = live
            ? call.lastMessage
              ? `${call.lastMessage.speaker === 'Caller' ? t('hotline_caller') : call.agent}: ${call.lastMessage.message}`
              : t('hotline_connecting')
            : call.summaryTitle || call.formTypeFormat;
          return (
            <button
              key={call.id}
              type="button"
              data-testid={`ai-call-${call.id}`}
              data-live={live || undefined}
              onClick={() => onSelect(call)}
              className={clsx(
                'w-full text-left px-4 py-3 border-b border-[#F0F0F0] dark:border-[#ffffff14] transition-colors',
                'hover:bg-[#F5F8FF] dark:hover:bg-[#1f1f1f] cursor-pointer',
                live && !active && 'bg-[#FFF7F6] dark:bg-[#2a1215]',
                active && 'bg-[#EAF1FF] dark:bg-[#0f2140] border-l-4 border-l-[#0364FF]',
                active && live && 'border-l-[#ff4d4f]',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-[15px] truncate">
                    {callerLabel(call)}
                  </p>
                  <p className="text-[12px] text-[#00000073] dark:text-[#ffffff73] truncate">
                    {call.callerNumber || call.callSid}
                  </p>
                </div>
                <span className="shrink-0 text-[12px] text-[#00000073] dark:text-[#ffffff73]">
                  {formatTs(call.startedAt || call.createdAt, 'DD/MM HH:mm')}
                </span>
              </div>
              <p
                className={clsx(
                  'mt-1 text-[13px] line-clamp-2',
                  live && !call.lastMessage
                    ? 'text-[#00000073] dark:text-[#ffffff73] italic'
                    : 'text-[#000000d9] dark:text-[#ffffffd9]',
                )}
              >
                {preview}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-1">
                {statusTag(call, t)}
                <Tag icon={<RobotOutlined />} bordered={false} className="rounded-full">
                  {call.formTypeFormat || call.formType}
                </Tag>
                {takeoverInProgress(call) ? (
                  <Tag
                    icon={<LoadingOutlined spin />}
                    color="purple"
                    bordered={false}
                    className="rounded-full"
                  >
                    {t('hotline_takingOver')}
                  </Tag>
                ) : (
                  isTransferred(call) && (
                    <Tag
                      icon={<SwapOutlined />}
                      color="purple"
                      bordered={false}
                      className="rounded-full"
                    >
                      {t('hotline_transferred')}
                    </Tag>
                  )
                )}
                {incomplete && !isTransferred(call) && (
                  <Tag color="error" bordered={false} className="rounded-full">
                    {t('hotline_incomplete')}
                  </Tag>
                )}
                {live && call.turns ? (
                  <span className="text-[12px] text-[#00000073] dark:text-[#ffffff73]">
                    {t('hotline_turns', { n: call.turns })}
                  </span>
                ) : null}
                {!live && call.durationSeconds ? (
                  <span className="text-[12px] text-[#00000073] dark:text-[#ffffff73]">
                    {formatDuration(call.durationSeconds)}
                  </span>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
